import dayjs, { Dayjs } from 'dayjs'

// `./locale` owns the dayjs plugin registration this module depends on
// (customParseFormat for `parseValue`, advancedFormat/weekYear for the week
// mask, quarterOfYear for `snapToPicker`). Importing `withLocale` from it is
// what pulls that side effect in.
import { withLocale } from './locale'

// `format`/`parse` for the DatePicker seam. Everything here goes through
// dayjs — the migration replaced antd's PANEL, not the date library.

export type PickerMode = 'time' | 'date' | 'week' | 'month' | 'quarter' | 'year'

/** antd's own `format` prop shape (rc-picker `FormatType`). */
export type FormatType = string | ((value: Dayjs) => string)
export type FormatProp =
  | FormatType
  | FormatType[]
  | { format: string; type?: 'mask' }

export type ShowTimeConfig = {
  format?: string
  showHour?: boolean
  showMinute?: boolean
  showSecond?: boolean
  use12Hours?: boolean
  hourStep?: number
  minuteStep?: number
  secondStep?: number
  hideDisabledOptions?: boolean
  defaultValue?: Dayjs | Dayjs[]
}

/** rc-picker's per-mode default input masks (no `showTime`). */
const DEFAULT_FORMAT: Record<PickerMode, string> = {
  time: 'HH:mm:ss',
  date: 'YYYY-MM-DD',
  week: 'gggg-wo',
  month: 'YYYY-MM',
  quarter: 'YYYY-[Q]Q',
  year: 'YYYY',
}

/**
 * The time part rc-picker appends to the date mask under `showTime`.
 *
 * `use12Hours` shapes the MASK only: the seam's time panel is a 0–23 hour
 * column with no AM/PM column, so `use12Hours` is one of the props `./index`
 * names as NOT ported. No call site passes it.
 */
export const timeFormatFor = (showTime: boolean | ShowTimeConfig): string => {
  const config = typeof showTime === 'object' ? showTime : {}
  if (config.format) return config.format
  const showHour = config.showHour ?? true
  const showMinute = config.showMinute ?? true
  const showSecond = config.showSecond ?? true
  const parts: string[] = []
  if (showHour) parts.push(config.use12Hours ? 'hh' : 'HH')
  if (showMinute) parts.push('mm')
  if (showSecond) parts.push('ss')
  const base = parts.join(':') || 'HH:mm:ss'
  return config.use12Hours ? `${base} A` : base
}

/**
 * Normalise `format` into the ordered mask list antd uses: the FIRST entry is
 * what the input displays, and every entry is accepted when parsing typed
 * text.
 */
export const resolveFormats = (
  format: FormatProp | undefined,
  picker: PickerMode,
  showTime?: boolean | ShowTimeConfig
): FormatType[] => {
  if (format != null) {
    if (Array.isArray(format))
      return format.length ? format : [DEFAULT_FORMAT[picker]]
    if (typeof format === 'object' && 'format' in format) return [format.format]
    return [format]
  }
  if (showTime && picker === 'date') {
    return [`${DEFAULT_FORMAT.date} ${timeFormatFor(showTime)}`]
  }
  return [DEFAULT_FORMAT[picker]]
}

/** Format one value with one mask (string mask or antd's function form). */
export const formatWith = (
  value: Dayjs,
  mask: FormatType,
  localeName?: string
): string =>
  typeof mask === 'function'
    ? mask(value)
    : withLocale(value, localeName).format(mask)

/** Display text for a value: the first mask wins, matching antd. */
export const formatValue = (
  value: Dayjs | null | undefined,
  masks: FormatType[],
  localeName?: string
): string => (value ? formatWith(value, masks[0], localeName) : '')

/**
 * Parse typed text back to a Dayjs.
 *
 * Strict parsing per string mask first (so "2026-07-15" never silently
 * becomes today under a loose parse), then one non-strict attempt as
 * rc-picker's fallback. Function masks can't be inverted directly, so the
 * loose attempt is the only way text written by one can come back — which is
 * why it is accepted ONLY when the parsed value formats back to exactly what
 * was typed. Without that round-trip a partially typed value dayjs happens to
 * understand ("2026", "2026-07") would commit a date the user never picked.
 */
export const parseValue = (
  text: string,
  masks: FormatType[],
  localeName?: string
): Dayjs | null => {
  const trimmed = text.trim()
  if (!trimmed) return null

  const stringMasks = masks.filter(
    (mask): mask is string => typeof mask === 'string'
  )

  for (let i = 0; i < stringMasks.length; i += 1) {
    const parsed = withLocale(
      dayjs(trimmed, stringMasks[i], true),
      localeName
    ) as Dayjs
    if (parsed.isValid()) return parsed
  }

  const loose = dayjs(trimmed)
  if (!loose.isValid()) return null
  const localized = withLocale(loose, localeName) as Dayjs
  const roundTrips = masks.some(
    (mask) => formatWith(localized, mask, localeName) === trimmed
  )
  return roundTrips ? localized : null
}

/**
 * Snap a value to the granularity its picker mode commits at, the way
 * rc-picker does before it calls `onChange` — a month picker emits the 1st of
 * the month, a year picker Jan 1, and so on. `date` keeps whatever time the
 * value carries (that is what `showTime` edits).
 */
export const snapToPicker = (value: Dayjs, picker: PickerMode): Dayjs => {
  switch (picker) {
    case 'month':
      return value.startOf('month')
    case 'quarter':
      return value.startOf('month')
    case 'year':
      return value.startOf('year')
    default:
      return value
  }
}

/**
 * Do two values address the same cell for this picker mode? Used to decide
 * whether a pick is a real change (antd only fires `onChange` on a change).
 */
export const isSameCell = (
  a: Dayjs | null | undefined,
  b: Dayjs | null | undefined,
  picker: PickerMode,
  showTime?: boolean | ShowTimeConfig
): boolean => {
  if (!a || !b) return !a && !b
  switch (picker) {
    case 'year':
      return a.isSame(b, 'year')
    case 'quarter':
      return a.isSame(b, 'year') && a.quarter() === b.quarter()
    case 'month':
      return a.isSame(b, 'month')
    case 'week':
      return a.isSame(b, 'day')
    case 'time':
      return a.isSame(b, 'second')
    default:
      return showTime ? a.isSame(b, 'second') : a.isSame(b, 'day')
  }
}
